import { Options, PlotOptions } from 'highcharts';
import moment from 'moment';
import { ProjectFinancialsZoomLevel } from 'api/project';
import { getDefaultScrollBarOptions } from 'components/organisms/dashboard/chart-options/utils';
import { queueMacrotask } from 'utils/macrotask';
import { ProjectFinancialsBaseChartProps } from '../../types';
import {
  projectFinancialsBaseChartOptions,
  projectFinancialsBaseXAxisOptions,
  setDateTimeChartExtremes,
} from './common';

const visibleDataCount = 6;

type ProjectFinancialsLineChartMeta = ProjectFinancialsBaseChartProps;

export const projectFinancialsBarChartOptions =
  ({
    currencyCode,
    revenueLabels,
    timeLabels,
    percentageLabels,
  }: ProjectFinancialsLineChartMeta) =>
  ({
    chartZoomLevel,
    extraPlotOptions,
    xAxisDates,
  }: {
    chartZoomLevel: ProjectFinancialsZoomLevel;
    extraPlotOptions?: PlotOptions;
    xAxisDates: number[];
  }): Options => {
    return {
      ...projectFinancialsBaseChartOptions({
        chartOptions: {
          type: 'column',
          events: {
            load: (e: any) => {
              e?.target.reflow();

              queueMacrotask(() => {
                const min = moment()
                  .startOf(
                    chartZoomLevel === ProjectFinancialsZoomLevel.Quarterly
                      ? 'quarter'
                      : 'month',
                  )
                  .startOf('day')
                  .valueOf();

                const max = moment()
                  .add(
                    visibleDataCount,
                    chartZoomLevel === ProjectFinancialsZoomLevel.Quarterly
                      ? 'quarters'
                      : 'months',
                  )
                  .startOf('day')
                  .valueOf();

                e?.target.xAxis[0].setExtremes(min, max);
              });
            },
          },
        },
        tooltipProps: {
          type: 'column',
          showX: false,
          currencyCode,
          revenueLabels,
          timeLabels,
          percentageLabels,
        },
      }),
      plotOptions: {
        column: {
          stacking: 'normal',
        },
        ...(extraPlotOptions ?? {}),
      },
      xAxis: projectFinancialsBaseXAxisOptions({
        events: {
          afterSetExtremes() {
            setDateTimeChartExtremes(
              this,
              xAxisDates,
              visibleDataCount,
              chartZoomLevel,
            );
          },
        },
        labels: {
          formatter: (obj) => {
            const valueMoment = moment(obj.value);

            if (chartZoomLevel === ProjectFinancialsZoomLevel.Quarterly) {
              return `Q${valueMoment.format("Q 'YY")}`;
            }

            return valueMoment.format("MMM 'YY");
          },
        },
        max: visibleDataCount,
      }),
      scrollbar: {
        enabled: xAxisDates.length > visibleDataCount,
        ...getDefaultScrollBarOptions(),
      },
    };
  };
